import { Input } from '@/widgets/components/input';
import get from 'lodash/get';
import merge from 'lodash/merge';
import set from 'lodash/set';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Button,
  Checkbox,
  DatePicker,
  InputPicker,
  Radio,
  RadioGroup,
} from 'rsuite';
import styled from 'styled-components';
import { cssVars } from '../../../app/styles';
import { IS_EDITING_FULL_NAME_FLAG } from '../../../constants';
import { dayjs } from '../../../utils/dayjs';
import { getLicenseExpirationDateString } from '../../utils/license-utils';

const RsModal = React.lazy(() => import('@/widgets/components/rs-modal'));
const RsModalHeader = React.lazy(
  () => import('@/widgets/components/rs-modal-header')
);
const RsModalFooter = React.lazy(
  () => import('@/widgets/components/rs-modal-footer')
);
const RsModalBody = React.lazy(
  () => import('@/widgets/components/rs-modal-body')
);

const Title = styled.h1`
  font-size: 18px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: 1;
  letter-spacing: normal;
  color: #101938;
  margin-bottom: 0;
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
  padding: 0;
`;

const Subtitle = styled.h2`
  padding-bottom: 10px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  color: #101938;
  width: 100%;
`;

const RemoveButton = styled(Button)`
  position: absolute;
  top: 0;
  right: 0;
  background: ${cssVars.color.deleteColor};
  color: #ffffff;
`;

const InputRow = styled.div`
  display: flex;
  gap: 10px;
  > * {
    flex: 1;
  }
`;

const InputColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const Section = styled.div`
  position: relative;
`;

function InputWithLabel({ label, value, onChange, ...inputProps }) {
  return (
    <div>
      <div>{label}</div>
      <Input onChange={onChange} value={value} {...inputProps} />
    </div>
  );
}

const StyledCheckbox = styled(Checkbox)`
  .rs-checkbox-checker {
    padding-left: 26px;
    .rs-checkbox-wrapper {
      left: 0;
    }
  }
`;

function InputWithLabelAndDefaultCheckbox({
  defaultValue,
  checkboxLabel,
  ...rest
}) {
  const [useDefault, setUseDefault] = useState(false);
  const { onChange } = rest;
  const onDefaultCheckboxChange = useCallback(
    (_, checked) => {
      setUseDefault(checked);
      if (checked && onChange) {
        onChange(defaultValue);
      }
    },
    [onChange, defaultValue]
  );
  return (
    <InputColumn>
      <InputWithLabel {...rest} />
      <StyledCheckbox checked={useDefault} onChange={onDefaultCheckboxChange}>
        {checkboxLabel}
      </StyledCheckbox>
    </InputColumn>
  );
}

function fieldProps(obj, field, callback, options = {}) {
  const { isReadOnly } = options;
  return {
    value: get(obj, field),
    onChange: !isReadOnly && ((value) => callback({ ...obj, [field]: value })),
    disabled: isReadOnly,
  };
}

const InputSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

function PersonDetails({
  person,
  propertyAddress,
  onChange,
  allowEditingName,
  allowEditingFullName,
  disabled,
}) {
  const personFieldProps = useCallback(
    (field, options) => fieldProps(person, field, onChange, options),
    [person, onChange]
  );

  const [isEditingFullName, setIsEditingFullName] = useState(
    !person?.firstName && !person?.lastName
  );
  const disableNameEditing = !allowEditingName || disabled;

  useEffect(() => {
    onChange({
      ...person,
      [IS_EDITING_FULL_NAME_FLAG]: isEditingFullName,
    });
  }, [isEditingFullName]);

  return (
    <InputSection>
      {allowEditingFullName && (
        <InputRow>
          <RadioGroup value={isEditingFullName} onChange={setIsEditingFullName}>
            <Radio value={false}>Separate First & Last Name</Radio>
            <Radio value={true}>Full Name</Radio>
          </RadioGroup>
        </InputRow>
      )}
      {!isEditingFullName && (
        <InputRow>
          <InputWithLabel
            label="First Name"
            {...personFieldProps('firstName', {
              isReadOnly: disableNameEditing,
            })}
          />
          <InputWithLabel
            label="Last Name"
            {...personFieldProps('lastName', {
              isReadOnly: disableNameEditing,
            })}
          />
        </InputRow>
      )}
      {isEditingFullName && (
        <InputWithLabel
          label={'Full Name'}
          {...personFieldProps('name', {
            isReadOnly: disableNameEditing || !allowEditingFullName,
          })}
        />
      )}

      <InputWithLabel
        label={'Company Name'}
        {...personFieldProps('companyName', { isReadOnly: disabled })}
      />
      <InputWithLabel
        label={'Email'}
        {...personFieldProps('email', { isReadOnly: disabled })}
      />

      <InputWithLabel
        label={'Phone Number'}
        {...personFieldProps('phone', { isReadOnly: disabled })}
      />

      <InputWithLabelAndDefaultCheckbox
        label={'Mailing Address'}
        {...personFieldProps('mailingAddress', { isReadOnly: disabled })}
        defaultValue={propertyAddress}
        checkboxLabel={'Same as rental address'}
      />
    </InputSection>
  );
}

const Buttons = styled.div`
  display: flex;
  gap: 10px;
  padding: 10px 0;
`;

const EditPane = styled.div`
  flex: 1;
  padding-right: 10px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Editor = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 10px;
`;

const LicenseEditorContent = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 500px;
`;

function stringToDate(s) {
  return dayjs(s).toDate();
}

function getEndOfDayFormattedDateString(d) {
  return `${dayjs(d).format('YYYY-MM-DD')}T23:59:59.000000`;
}

function EnumFieldEditor({ type, value, onChange }) {
  return (
    <InputPicker
      value={value}
      data={
        type?.enumValues?.map((option) => ({ label: option, value: option })) ||
        []
      }
      onChange={onChange}
    />
  );
}

const FIELD_TYPES = {
  ENUM: 'enum',
};

const FIELD_TYPE_TO_EDITOR = {
  [FIELD_TYPES.ENUM]: EnumFieldEditor,
};

function FieldEditor({ data, type, fieldPath, onChangeData, label }) {
  const fieldPathString = useMemo(
    () => fieldPath.reduce((acc, field) => `${acc}["${field}"]`, ''),
    [fieldPath]
  );

  const value = get(data, fieldPathString);

  const EditorComponent = FIELD_TYPE_TO_EDITOR[type?.typeName];

  const getChangedData = useCallback(
    (newValue, path) => set({}, path, newValue),
    []
  );

  if (!EditorComponent) {
    return null;
  }

  return (
    <EditorComponent
      label={label}
      type={type}
      value={value}
      onChange={(newValue) =>
        onChangeData(getChangedData(newValue, fieldPathString))
      }
    />
  );
}

export function LicenseEditorUI({
  show,
  onHide,
  license,
  onChange,
  allowEditingExpiryDate,
  allowEditingName,
  allowEditingFullName,
  allowEditingOwner,
  licenseEditableFields,
  showPeopleEditor,
}) {
  const [registrant, setRegistrant] = useState({});
  const [owners, setOwners] = useState([]);

  const [isExpirationDataEditable, setIsExpirationDateEditable] =
    useState(false);
  const [editedExpirationDate, setEditedExpirationDate] = useState();
  const [editedMiscFields, setEditedMiscFields] = useState({ misc: {} });

  useEffect(() => {
    setRegistrant(license?.people?.registrant);
    setOwners(license?.people?.owner || []);
  }, [license]);

  useEffect(() => {
    setEditedExpirationDate(
      stringToDate(getLicenseExpirationDateString(license))
    );
  }, [isExpirationDataEditable, license]);

  const onConfirmClick = useCallback(() => {
    if (!onChange) return;

    const editedLicense = {
      ...license,
      people: {
        ...license?.people,
        registrant,
      },
      certificateInfo: {
        ...license?.certificateInfo,
        ...(allowEditingExpiryDate &&
          isExpirationDataEditable && {
            expirationDate:
              getEndOfDayFormattedDateString(editedExpirationDate),
          }),
      },
      misc: {
        ...license.misc,
        ...editedMiscFields.misc,
      },
    };

    if (allowEditingOwner) {
      editedLicense.people.owner = owners;
    }

    onChange({ editedLicense, updatedMiscFields: editedMiscFields.misc });
  }, [
    onChange,
    license,
    registrant,
    owners,
    allowEditingOwner,
    allowEditingExpiryDate,
    isExpirationDataEditable,
    editedExpirationDate,
    editedMiscFields,
  ]);

  const expirationDateToDisplay = useMemo(
    () =>
      isExpirationDataEditable
        ? editedExpirationDate
        : stringToDate(getLicenseExpirationDateString(license)),
    [editedExpirationDate, isExpirationDataEditable, license]
  );

  const propertyAddress = license?.propertyInfo?.propertyAddress;

  const REMOVE_OWNER = undefined;
  const editOrRemoveOwner = useCallback(
    (changedOwner, ownerIndex) => {
      setOwners((oldOwners) => {
        const newOwners = oldOwners.slice();
        if (changedOwner) {
          newOwners.splice(ownerIndex, 1, changedOwner);
        } else if (ownerIndex > 0) {
          newOwners.splice(ownerIndex, 1);
        }
        return newOwners;
      });
    },
    [license]
  );

  const addOwner = useCallback(
    () => setOwners((oldOwners) => [...oldOwners, {}]),
    []
  );
  const miscFieldsToDisplay = useMemo(
    () => merge({}, { misc: license.misc }, editedMiscFields),
    [license.misc, editedMiscFields]
  );

  return (
    <Suspense fallback={<div />}>
      <RsModal
        open={show}
        onClose={onHide}
        overflow
        css={`
          .rs-modal-content {
            padding-left: ${cssVars.rsModalPaddingLR};
            padding-right: ${cssVars.rsModalPaddingLR};
            width: 760px;
          }
        `}
      >
        <LicenseEditorContent>
          <RsModalHeader>
            <Title>Update License Application</Title>
          </RsModalHeader>

          <RsModalBody>
            <EditPane>
              {showPeopleEditor && (
                <Section>
                  <Subtitle>Permit Holder</Subtitle>
                  <PersonDetails
                    person={registrant}
                    onChange={setRegistrant}
                    propertyAddress={propertyAddress}
                    allowEditingName={allowEditingName}
                    allowEditingFullName={allowEditingFullName}
                  />
                </Section>
              )}

              {showPeopleEditor &&
                allowEditingOwner &&
                Array.isArray(owners) && (
                  <>
                    {owners.map((owner, i) => (
                      <Section key={i}>
                        <Subtitle>Owner #{i + 1}</Subtitle>
                        {i === 0 &&
                        license?.certificateInfo?.registrantType === 'owner' ? (
                          <p>Same as permit holder</p>
                        ) : (
                          <PersonDetails
                            person={owner}
                            onChange={(newOwner) =>
                              editOrRemoveOwner(newOwner, i)
                            }
                            propertyAddress={propertyAddress}
                            allowEditingName={allowEditingName}
                          />
                        )}

                        {i > 0 && (
                          <RemoveButton
                            color="error"
                            onClick={() => editOrRemoveOwner(REMOVE_OWNER, i)}
                          >
                            Remove
                          </RemoveButton>
                        )}
                      </Section>
                    ))}
                    <Button onClick={addOwner}>Add Owner</Button>
                  </>
                )}

              {allowEditingExpiryDate && (
                <Section>
                  <Subtitle>License Details</Subtitle>
                  <Editor>
                    <Checkbox
                      value={isExpirationDataEditable}
                      onChange={(value, checked) =>
                        setIsExpirationDateEditable(checked)
                      }
                    >
                      Change expiration date:
                    </Checkbox>
                    <DatePicker
                      placement="bottomStart"
                      value={expirationDateToDisplay}
                      onChange={
                        isExpirationDataEditable && setEditedExpirationDate
                      }
                      disabled={!isExpirationDataEditable}
                    />
                  </Editor>
                </Section>
              )}
              {licenseEditableFields &&
                licenseEditableFields.map(({ fieldPath, label, type }) => (
                  <Section key={label}>
                    <Subtitle>{label}</Subtitle>
                    <Editor>
                      <FieldEditor
                        fieldPath={fieldPath}
                        data={miscFieldsToDisplay}
                        onChangeData={(changeset) =>
                          setEditedMiscFields(
                            merge({}, editedMiscFields, changeset)
                          )
                        }
                        label={label}
                        type={type}
                      />
                    </Editor>
                  </Section>
                ))}
            </EditPane>
          </RsModalBody>

          <RsModalFooter>
            <Buttons>
              <Button onClick={onConfirmClick} appearance="primary">
                Confirm
              </Button>
              <Button onClick={onHide}>Cancel</Button>
            </Buttons>
          </RsModalFooter>
        </LicenseEditorContent>
      </RsModal>
    </Suspense>
  );
}
